import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Factory, MapPin } from "lucide-react";

// Positions are % coordinates calibrated onto the Iraq governorate map (/iraq-map.jpg).
const LOCATIONS = [
  { id: "sulaimani", name: "Sulaimani", x: 64.1, y: 28.4, plant: true, note: "Our founding manufacturing plant (since 2009), serving the Northern Region with production, distribution and technical support." },
  { id: "erbil", name: "Erbil", x: 52.0, y: 22.0, note: "Full product coverage and on-site technical support across the Northern Region." },
  { id: "kirkuk", name: "Kirkuk", x: 55.7, y: 30.1, note: "Reliable supply and technical assistance for ready-mix and construction projects." },
  { id: "baghdad", name: "Baghdad", x: 57.0, y: 55.0, plant: true, note: "State-of-the-art manufacturing plant (since 2018) and central distribution hub for the capital and central Iraq." },
  { id: "karbala", name: "Karbala", x: 54.8, y: 63.3, note: "Product coverage and mix-design support for the holy governorates." },
  { id: "hilla", name: "Hilla", x: 58.2, y: 64.6, note: "Coverage and technical support across Babil and surrounding areas." },
  { id: "najaf", name: "Najaf", x: 57.8, y: 70.3, note: "Prompt delivery and engineering support for regional projects." },
  { id: "diwaniya", name: "Diwaniya", x: 62.6, y: 69.9, note: "Coverage and technical support across the Middle Euphrates." },
  { id: "basrah", name: "Basrah", x: 87.0, y: 85.0, note: "Product coverage for the South, including durable solutions for aggressive coastal environments." },
];

export const IraqMap = () => {
  const [activeId, setActiveId] = useState("baghdad");
  const active = LOCATIONS.find((l) => l.id === activeId);

  return (
    <div className="grid gap-8 lg:grid-cols-2 items-center" data-testid="iraq-map">
      {/* Map */}
      <div className="rounded-2xl bg-white border border-slate-200 p-3 shadow-xl">
        <div className="relative">
          <img src="/iraq-map.jpg" alt="HMA coverage map of Iraq" className="w-full h-auto rounded-lg select-none" draggable="false" />
          {LOCATIONS.map((l) => {
            const isActive = l.id === activeId;
            const labelLeft = l.x > 75;
            return (
              <button
                key={l.id}
                onClick={() => setActiveId(l.id)}
                data-testid={`map-pin-${l.id}`}
                style={{ left: `${l.x}%`, top: `${l.y}%` }}
                className="absolute -translate-x-1/2 -translate-y-1/2 flex items-center"
                aria-label={l.name}
              >
                {l.plant && <span className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-gold-500/50 ${isActive ? "w-7 h-7" : "w-5 h-5"}`} />}
                <span className={`rounded-full border-2 border-white shadow-md transition-all ${l.plant ? "bg-gold-500" : "bg-brand-500"} ${isActive ? "w-4 h-4" : "w-3 h-3"}`} />
                <span
                  className={`absolute ${labelLeft ? "right-4" : "left-4"} whitespace-nowrap text-[11px] sm:text-xs font-bold px-1 rounded ${isActive ? "text-white bg-white/80" : "text-slate-600"}`}
                >
                  {l.name}
                </span>
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-4 mt-3 px-1 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-gold-500 inline-block" /> Manufacturing Plant</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-brand-500 inline-block" /> Coverage Area</span>
        </div>
      </div>

      {/* List + detail */}
      <div>
        <div className="flex flex-wrap gap-2">
          {LOCATIONS.map((l) => (
            <button
              key={l.id}
              onClick={() => setActiveId(l.id)}
              data-testid={`map-btn-${l.id}`}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm border transition-colors ${
                activeId === l.id ? "bg-brand-500 border-brand-500 text-white" : "bg-white border-slate-300 text-slate-600 hover:border-brand-500/50"
              }`}
            >
              {l.plant ? <Factory size={14} /> : <MapPin size={14} />} {l.name}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={active.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="mt-6 p-6 rounded-xl bg-white border border-slate-200"
            data-testid="map-detail"
          >
            <div className="flex items-center gap-3">
              <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${active.plant ? "bg-gold-500/10 text-gold-400 border border-gold-500/20" : "bg-brand-500/10 text-brand-400 border border-brand-500/20"}`}>
                {active.plant ? <Factory size={20} /> : <MapPin size={20} />}
              </div>
              <div>
                <h3 className="font-heading font-semibold text-slate-900 text-lg">{active.name}</h3>
                <span className="text-xs font-mono text-brand-400">{active.plant ? "Manufacturing Plant" : "Coverage & Support"}</span>
              </div>
            </div>
            <p className="mt-4 text-sm text-slate-500 leading-relaxed">{active.note}</p>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};
